import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, getDocs, query, where, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';
import { handleError } from '../utils/errorHandler';
import { firestoreWithRetry, isRetryableError } from '../utils/retryWithBackoff';
import toast from 'react-hot-toast';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) {
      setFavorites([]);
      return undefined;
    }
    loadFavorites(currentUser.uid);
    return undefined;
    // loadFavorites is recreated each render; reload only when the user changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  // Charger les favoris
  const loadFavorites = async (userId) => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const favoritesQuery = query(
        collection(db, 'favorites'),
        where('userId', '==', userId)
      );
      
      // Utiliser retry pour le chargement aussi
      const favoritesSnapshot = await firestoreWithRetry(async () => {
        return await getDocs(favoritesQuery);
      }, {
        maxRetries: 2,
        initialDelay: 1000,
        maxDelay: 5000
      });
      
      const favoritesData = favoritesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFavorites(favoritesData);
      return favoritesData;
    } catch (error) {
      logger.error(error, { context: { action: 'loadFavorites', userId } });
      
      // Ne pas afficher de toast pour les erreurs de chargement (moins critique)
      if (!isRetryableError(error)) {
        handleError(error, { showToast: false });
      }
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Ajouter un favori avec optimistic update
  const addFavorite = async (userId, itemId, itemType = 'business') => {
    // Vérifier si déjà en favori
    if (isFavorite(userId, itemId)) {
      logger.warn('Item already in favorites');
      return null;
    }

    // Optimistic update - ajouter immédiatement à l'UI
    const tempId = `temp_${Date.now()}`;
    const tempFavorite = {
      id: tempId,
      userId,
      itemId,
      itemType,
      createdAt: new Date(),
      isOptimistic: true
    };
    
    const previousFavorites = [...favorites];
    setFavorites(prev => [...prev, tempFavorite]);

    try {
      // Opération réelle avec retry automatique pour les erreurs réseau
      const favoriteData = {
        userId,
        itemId,
        itemType,
        createdAt: new Date()
      };
      
      const docRef = await firestoreWithRetry(async () => {
        return await addDoc(collection(db, 'favorites'), favoriteData);
      }, {
        maxRetries: 3,
        initialDelay: 1000,
        maxDelay: 8000
      });
      
      // Remplacer le favori temporaire par le vrai
      setFavorites(prev => prev.map(fav => 
        fav.id === tempId 
          ? { id: docRef.id, ...favoriteData }
          : fav
      ));
      
      toast.success('Ajouté aux favoris');
      logger.log('Favorite added successfully', { userId, itemId });
      return docRef.id;
    } catch (error) {
      // Rollback en cas d'erreur
      setFavorites(previousFavorites);
      
      // Message d'erreur personnalisé selon le type d'erreur
      if (isRetryableError(error)) {
        toast.error(
          'Erreur de connexion réseau. Veuillez vérifier votre connexion internet et réessayer.',
          { duration: 5000 }
        );
        logger.error(error, { 
          context: { action: 'addFavorite', userId, itemId },
          message: 'Network error - retries exhausted'
        });
      } else {
        handleError(error, { 
          context: { action: 'addFavorite', userId, itemId },
          showToast: true
        });
      }
      throw error;
    }
  };

  // Supprimer un favori avec optimistic update
  const removeFavorite = async (favoriteId) => {
    // Optimistic update - supprimer immédiatement de l'UI
    const favoriteToRemove = favorites.find(fav => fav.id === favoriteId);
    const previousFavorites = [...favorites];
    
    setFavorites(prev => prev.filter(fav => fav.id !== favoriteId));

    try {
      // Opération réelle (sauf si c'était un favori optimiste) avec retry
      if (!favoriteToRemove?.isOptimistic) {
        await firestoreWithRetry(async () => {
          return await deleteDoc(doc(db, 'favorites', favoriteId));
        }, {
          maxRetries: 3,
          initialDelay: 1000,
          maxDelay: 8000
        });
      }
      
      toast.success('Retiré des favoris');
      logger.log('Favorite removed successfully', { favoriteId });
    } catch (error) {
      // Rollback en cas d'erreur
      setFavorites(previousFavorites);
      
      // Message d'erreur personnalisé selon le type d'erreur
      if (isRetryableError(error)) {
        toast.error(
          'Erreur de connexion réseau. Veuillez vérifier votre connexion internet et réessayer.',
          { duration: 5000 }
        );
        logger.error(error, { 
          context: { action: 'removeFavorite', favoriteId },
          message: 'Network error - retries exhausted'
        });
      } else {
        handleError(error, { 
          context: { action: 'removeFavorite', favoriteId },
          showToast: true
        });
      }
      throw error;
    }
  };

  // Vérifier si un élément est en favori
  const isFavorite = (userId, itemId) => {
    return favorites.some(fav => fav.userId === userId && fav.itemId === itemId);
  };

  // Wrapper pour compatibilité avec BusinessCard (accepte un objet business)
  const addToFavorites = async (business) => {
    if (!currentUser) {
      toast.error('Veuillez vous connecter pour ajouter aux favoris');
      return;
    }
    if (business && business.id) {
      return await addFavorite(currentUser.uid, business.id, 'business');
    }
  };

  // Wrapper pour compatibilité avec BusinessCard (accepte un itemId)
  const removeFromFavorites = async (itemId) => {
    if (!currentUser) {
      return;
    }
    // Trouver le favori correspondant
    const favorite = favorites.find(fav => fav.userId === currentUser.uid && fav.itemId === itemId);
    if (favorite) {
      return await removeFavorite(favorite.id);
    }
  };

  const value = {
    favorites,
    loading,
    loadFavorites,
    addFavorite,
    removeFavorite,
    isFavorite,
    addToFavorites,
    removeFromFavorites,
    // Pour compatibilité avec CustomListsManager
    lists: [],
    createList: async () => Promise.resolve(),
    deleteList: async () => Promise.resolve(),
    addBusinessToList: async () => Promise.resolve(),
    removeBusinessFromList: async () => Promise.resolve(),
    exportList: async () => Promise.resolve()
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

